/**
 * Job Notification Service
 * Handles browser push notifications and in-app toasts for async job progress
 */

class JobNotificationService {
  constructor() {
    this.permission = 'default';
    this.activePolls = new Map();
    this.onJobComplete = null;
    this.onJobProgress = null;
  }

  /**
   * Request browser notification permission
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Show browser notification
   */
  showNotification(title, options = {}) {
    if (this.permission !== 'granted') return;

    const notification = new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: options.tag || 'hospiceguard',
      requireInteraction: options.requireInteraction || false,
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) options.onClick();
    };

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000);

    return notification;
  }

  /**
   * Start polling for job status using SSE
   */
  startJobTracking(jobId, token, callbacks = {}) {
    const { onProgress, onComplete, onError } = callbacks;
    
    // Use SSE for real-time updates
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const eventSource = new EventSource(
      `${API_URL}/api/async-docs/status/${jobId}/stream`
    );

    // SSE doesn't support headers directly, fall back to polling
    this.startPolling(jobId, token, callbacks);
  }

  /**
   * Start polling for job status
   */
  startPolling(jobId, token, callbacks = {}) {
    const { onProgress, onComplete, onError } = callbacks;
    
    if (this.activePolls.has(jobId)) {
      return; // Already polling
    }

    const poll = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/async-docs/status/${jobId}`);

        if (!response.ok) {
          throw new Error('Failed to get job status');
        }

        const data = await response.json();

        // Call progress callback
        if (onProgress) {
          onProgress(data);
        }

        // Check for completion
        if (['COMPLETED', 'FAILED', 'TIMEOUT', 'CANCELLED'].includes(data.status)) {
          this.stopPolling(jobId);

          if (data.status === 'COMPLETED') {
            // Show browser notification
            this.showNotification('Document Processing Complete', {
              body: `${data.filename || 'Your document'} has been processed successfully.`,
              tag: `job-${jobId}`,
              onClick: () => {
                // Navigate to results if needed
              }
            });

            if (onComplete) onComplete(data);
          } else {
            // Show error notification
            this.showNotification('Document Processing Failed', {
              body: data.error || `Processing ${data.status.toLowerCase()}`,
              tag: `job-${jobId}`
            });

            if (onError) onError(data);
          }
          return;
        }

        // Continue polling
        const pollInfo = this.activePolls.get(jobId);
        if (pollInfo) {
          pollInfo.timeoutId = setTimeout(poll, 2000); // Poll every 2 seconds
        }

      } catch (error) {
        console.error('Job polling error:', error);
        if (onError) onError({ error: error.message });
        this.stopPolling(jobId);
      }
    };

    // Start polling
    this.activePolls.set(jobId, { timeoutId: null });
    poll();
  }

  /**
   * Stop polling for a job
   */
  stopPolling(jobId) {
    const pollInfo = this.activePolls.get(jobId);
    if (pollInfo && pollInfo.timeoutId) {
      clearTimeout(pollInfo.timeoutId);
    }
    this.activePolls.delete(jobId);
  }

  /**
   * Stop all active polls
   */
  stopAllPolling() {
    this.activePolls.forEach((_, jobId) => this.stopPolling(jobId));
  }

  /**
   * Get count of active jobs being tracked
   */
  getActiveJobCount() {
    return this.activePolls.size;
  }
}

// Singleton instance
const jobNotificationService = new JobNotificationService();

export default jobNotificationService;
export { JobNotificationService };
